import { useEffect, useRef, useState } from "react";
import {
  getDayDifference,
  getUserName,
  renderTextWithLinks,
} from "../../../../helpers";
import {
  BtnLoader,
  CaretRight,
  DeleteBin,
  DeletedContent,
  Heart,
  More,
  Siren,
} from "../../../../icons";
import ProfileImageComp from "../../../common/profile";
import {
  useAddFeedReactionMutation,
  useLazyGetCommentsQuery,
} from "../../../../store/api/homeApi";
import Button from "../../../common/button";
import ReplyComment from "./reply-comment";

import { toastError } from "../../../toast";
import ProfileBadgeAvatar from "../../../user/user-profile/profile-avatar";
import { Link } from "react-router-dom";
import { ROUTES } from "../../../../constants/routes";
import ExpandableParagraph from "./expandable-paragraph";
import { useAppDispatch } from "../../../../hooks/store-hooks";
import { getLocalStorage } from "../../../../helpers/storage";
import { openModal } from "../../../../store/slices/modalSlice";
import { MODAL_NAME } from "../../../../constants";

interface CommentProps {
  commentData: any;
  postId: string;
  setReplyCommentId: React.Dispatch<React.SetStateAction<null>>;
  replyCommentId: string | null;
  reply_count: number;
  deletedCommentInfo: {
    postId: string;
    commentId: string;
    reactionType?: string;
  } | null;
}

const Comment: React.FC<CommentProps> = ({
  commentData,
  postId,
  deletedCommentInfo,
}) => {
  const dispatch = useAppDispatch();
  const user = JSON.parse(getLocalStorage("userData") || "{}");
  const [newReply, setNewReply] = useState("");
  const [replyData, setReplyData]: any = useState(null);
  const [showAllReplies, setShowAllReplies] = useState(false);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const {
    id: commentId,
    user: commentUser,
    reaction_message,
    like_count,
    liked,
    created_at,
  } = commentData || {};

  const [addFeedReaction, { isLoading }] = useAddFeedReactionMutation();
  const [trigger, { isFetching }] = useLazyGetCommentsQuery();

  const [likeData, setLikeData] = useState({
    like: false,
    count: 0,
  });

  useEffect(() => {
    setLikeData({
      ...likeData,
      like: liked,
      count: like_count,
    });
  }, [like_count, liked]);

  useEffect(() => {
    fetchReplys();
  }, [commentId]);

  const fetchReplys = async () => {
    try {
      const response = await trigger({
        entityId: commentId,
        reactionOffset: 0,
        reactionLimit: 10,
      }).unwrap();
      if (response.success) {
        setReplyData(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLike = async () => {
    try {
      const response = await addFeedReaction({
        activityId: postId,
        bodyData: { type: "like", parent_id: commentId },
      }).unwrap();
      if (response.success) {
        setLikeData({
          ...likeData,
          like: !likeData.like,
          count: likeData.like ? likeData.count - 1 : likeData.count + 1,
        });
      } else {
        toastError({ message: response?.error?.message });
      }
    } catch (error) {
      toastError({
        message: error,
      });
    }
  };

  const getMoreReplies = async () => {
    try {
      const response = await trigger({
        entityId: commentId,
        reactionOffset: replyData?.reactions?.length || 0,
      }).unwrap();
      handleReplyStorage(response.data, false);
    } catch (error) {
      toastError({ message: error });
    }
  };

  const handleReplyStorage = (replyData: any, isReset = false) => {
    if (isReset) {
      setReplyData(replyData);
    } else {
      setReplyData((prev: any) => ({
        ...prev,
        reactions: [...prev.reactions, ...replyData.reactions],
        is_end: replyData.is_end,
      }));
    }
  };

  const handleAddReply = async () => {
    if (newReply?.length === 0) return;
    try {
      const bodyData = {
        type: "reply",
        parent_id: commentId,
        data: { message: newReply },
      };
      const response = await addFeedReaction({
        activityId: postId,
        bodyData,
      }).unwrap();
      if (response.success) {
        setNewReply("");
        fetchReplys(); // Refetch replies after adding new one
      } else {
        toastError({ message: response?.error?.message });
      }
    } catch (error) {
      toastError({
        message: error,
      });
    }
  };

  const handleReplyClick = () => {
    setShowAllReplies(!showAllReplies);
    if (!showAllReplies) {
      setTimeout(() => {
        replyInputRef.current?.focus();
      }, 0);
    }
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [moreOptions, setmoreOptions] = useState(false);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setmoreOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [moreOptions]);

  const renderReplies = () => {
    if (!replyData?.reactions?.length) return null;

    const firstReply = replyData?.reactions?.[0];

    return (
      <>
        {firstReply && (
          <ReplyComment
            replyData={firstReply}
            commentId={firstReply.id}
            postId={postId}
            handleReplyClick={handleReplyClick}
          />
        )}

        {showAllReplies &&
          replyData?.reactions
            .slice(1)
            .map((reply: any) => (
              <ReplyComment
                key={reply.id}
                replyData={reply}
                commentId={reply.id}
                postId={postId}
                handleReplyClick={handleReplyClick}
              />
            ))}

        {!showAllReplies && replyData?.reactions?.length > 1 && (
          <div
            className="flex justify-center text-sm mb-10 cursor-pointer text-primary-900 font-bold"
            onClick={() => setShowAllReplies(true)}
          >
            {isFetching ? (
              <BtnLoader />
            ) : (
              `Show ${replyData?.reactions?.length - 1} more replies`
            )}
          </div>
        )}

        {!replyData?.is_end && showAllReplies && (
          <div
            className="flex justify-center text-sm mb-10 cursor-pointer text-primary-900 font-bold"
            onClick={getMoreReplies}
          >
            {isFetching ? <BtnLoader /> : "Load More Replies"}
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    if (
      deletedCommentInfo &&
      deletedCommentInfo.postId === postId &&
      deletedCommentInfo.reactionType === "reply"
    ) {
      setReplyData((prevReplyData: any) => {
        if (!prevReplyData?.reactions) return prevReplyData;
        return {
          ...prevReplyData,
          reactions: prevReplyData.reactions.filter(
            (reply: any) => reply.id !== deletedCommentInfo.commentId,
          ),
        };
      });
    }
  }, [deletedCommentInfo, postId]);

  return (
    <div key={commentId} className="rounded-lg mt-5">
      {commentData?.is_deleted ? (
        <div className="flex justify-start items-center bg-[#fdeded] p-4 rounded-xl mt-5 gap-2">
          <DeletedContent />
          <p className="text-primary-900 font-normal text-md">
            {reaction_message}
          </p>
        </div>
      ) : (
        <>
          <div className="flex mb-2">
            {commentUser?.badge ? (
              <Link
                to={ROUTES.USER_PROFILE.replace(":user_id", commentUser?.id)}
              >
                <div className={`relative w-10 h-[40px] mr-2`}>
                  <ProfileBadgeAvatar
                    profileImage={commentUser?.profile_picture}
                    customDivClassName="absolute h-10 w-10 border-4 z-1"
                    customImgClassName="h-[40px] w-[40px] absolute rounded-full p-[3px]"
                    customIconDivClassName="absolute z-[120] -bottom-[5px] left-[15px]"
                    customStyle1="w-[50px] h-[14px] absolute -bottom-2 z-[100] overflow-hidden"
                    customStyle2="w-[200px] h-4 absolute top-3 z-10 -left-10 overflow-hidden"
                    customStarWidth={10}
                    customStarHeight={10}
                    customStyle3="w-[34px] h-[34px]"
                    badge={commentUser?.badge}
                  />
                </div>
              </Link>
            ) : (
              <Link
                to={ROUTES.USER_PROFILE.replace(":user_id", commentUser?.id)}
              >
                <ProfileImageComp
                  imageUrl={commentUser?.profile_picture}
                  customClass="w-10 h-10"
                  is_opc={commentUser?.memberships?.is_opc}
                />
              </Link>
            )}

            <div className="flex-1 rounded-xl-10 bg-primary-300 p-3">
              <div className="flex items-center justify-between">
                <Link
                  to={ROUTES.USER_PROFILE.replace(":user_id", commentUser?.id)}
                >
                  <div className="text-sm font-semibold">
                    {getUserName(commentUser)}
                  </div>
                  <p className="text-xs text-primary-500 text-fs-11">
                    {commentUser?.designation}
                  </p>
                </Link>
                <div className="flex flex-row items-center gap-3 relative">
                  <p className="text-xs text-gray-500">
                    {getDayDifference(created_at)}
                  </p>
                  <div
                    className="hover:cursor-pointer"
                    onClick={() => {
                      setmoreOptions(true);
                    }}
                  >
                    <More />
                  </div>
                  {commentUser?.id === user?._id ? (
                    <div
                      ref={wrapperRef}
                      className={`absolute z-20 right-0 flex flex-col items-start justify-start bg-white shadow top-8 w-52 rounded-xl ${moreOptions ? "" : "hidden"}`}
                    >
                      <button
                        type="button"
                        className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
                        onClick={() => {
                          dispatch(
                            openModal({
                              modalName: MODAL_NAME.DELETE_REACTION,
                              modalData: {
                                entityId: commentId,
                                reactionType: "comment",
                                postId: postId,
                              },
                            }),
                          );
                        }}
                      >
                        <DeleteBin width={18} height={18} />
                        <div className="text-base font-normal leading-tight text-black">
                          Delete Comment
                        </div>
                      </button>
                    </div>
                  ) : (
                    <div
                      ref={wrapperRef}
                      className={`absolute z-20 right-0 flex flex-col items-start justify-start bg-white shadow top-5 w-52 rounded-xl ${moreOptions ? "" : "hidden"}`}
                    >
                      <button
                        onClick={() => {
                          dispatch(
                            openModal({
                              modalName: MODAL_NAME.REPORT_REACTION,
                              modalData: {
                                entityId: commentId,
                                reactionType: "comment",
                              },
                            }),
                          );
                        }}
                        type="button"
                        className="w-full p-4 border-b border-[#ebebeb] justify-start items-center gap-2 inline-flex"
                      >
                        <Siren />
                        <div className="text-base font-normal leading-tight text-black">
                          Report Comment
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-md mt-2 text-primary-900 leading-5">
                <ExpandableParagraph
                  text={renderTextWithLinks(reaction_message || "")}
                />
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-gray-500 text-sm !ml-12">
            <div
              className="flex items-center space-x-1 cursor-pointer"
              onClick={handleLike}
            >
              <Heart
                fillColor={likeData?.like ? "#FF5147" : "#ffffff"}
                strokeColor={likeData?.like ? "#FF5147" : "#343330"}
              />
              <span>{likeData?.count || 0}</span>
            </div>
            <div>|</div>
            <button onClick={handleReplyClick}>Reply</button>
          </div>
        </>
      )}
      {showAllReplies && (
        <div className="flex mt-2 ml-10 justify-end mb-10">
          <textarea
            ref={replyInputRef}
            value={newReply}
            onChange={(e) => {
              setNewReply(e.target.value);
              const target = e.target as HTMLTextAreaElement;
              target.style.height = newReply ? "auto" : "20px";
              const maxHeight = 80;
              target.style.height =
                Math.min(target.scrollHeight, maxHeight) + "px";
            }}
            className="border border-gray-300 rounded-full w-[84%] p-2 px-4 outline-none resize-none"
            placeholder="Add a reply..."
            rows={1}
            style={{
              overflow: "hidden",
              lineHeight: "20px",
              height: "20px",
              minHeight: "40px",
              maxHeight: "60px",
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              const maxHeight = 80;
              target.style.height =
                Math.min(target.scrollHeight, maxHeight) + "px";
            }}
          />
          <Button
            onClick={handleAddReply}
            isSubmitting={isLoading}
            customClasses="bg-secondary-300 text-white w-10 h-10 rounded-full ml-2 flex items-center justify-center cursor-pointer"
          >
            <CaretRight />
          </Button>
        </div>
      )}
      <div className="ml-10">{renderReplies()}</div>
    </div>
  );
};

export default Comment;
